import { Col, Container, Row, Spacer } from "@amzn/stencil-react-components/layout";
import { Link } from "@amzn/stencil-react-components/link";
import { Text } from "@amzn/stencil-react-components/text";
import { ToggleButton, ToggleButtonGroup } from "@amzn/stencil-react-components/toggle-button";
import { MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { MessageBannersBox } from "@amzn/it-shop-shared-components";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import { useOrdersQuery } from "@/hooks/use-orders-query";
import { useNotificationStore } from "@/stores/use-notification-store";
import React, { FC, useState } from "react";
import { HardwareOrdersTable } from "./components/HardwareOrdersTable/hardware-orders-table";
import { HardwareSupportFlyout } from "@/components/HardwareSupportFlyout/HardwareSupportFlyout";
import { useLocation } from "react-router-dom";

const SOFTWARE_ORDERS_URL = "https://shop.it.a2z.com/orders?tab=0";

export const OrdersPage: FC = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const [showFlyout, setShowFlyout] = useState(params.get("flyoutHardwareSupport") === "true");
  const { data: orders = [], isLoading, error: fetchError } = useOrdersQuery();
  const banners = useNotificationStore((state) => state.banners);
  const removeBanner = useNotificationStore((state) => state.removeBanner);
  const addBanner = useNotificationStore((state) => state.addBanner);

  React.useEffect(() => {
    if (fetchError) {
      addBanner({ type: "error", message: "Something went wrong loading your orders. Please try again later." });
    }
  }, [fetchError, addBanner]);

  const bannerTypeMap: Record<string, MessageBannerType> = {
    error: MessageBannerType.Error,
    success: MessageBannerType.Success,
    warning: MessageBannerType.Warning,
    informational: MessageBannerType.Informational,
  };

  const mapBannersToMessages = () =>
    banners.map((banner) => ({
      id: banner.id,
      message: banner.message,
      type: bannerTypeMap[banner.type] ?? MessageBannerType.Informational,
    }));

  const handleToggleChange = (_event: React.FormEvent<HTMLElement>, value: string | string[] | undefined) => {
    if (value === "software") {
      window.location.href = SOFTWARE_ORDERS_URL;
    }
  };

  return (
    <Container isFullWidth={false}>
      <Col gridGap="S200" width="100%">
        <Row padding="S300" gridGap="S400" width="100%">
          <Text fontSize="T400" color="neutral90">
            {getString("myOrders.topBar")}
          </Text>
        </Row>

        <Row gridGap="S400" width="100%" justifyContent="space-between" alignItems="center">
          <ToggleButtonGroup onChange={handleToggleChange} data-testid="orders-toggle">
            <ToggleButton isSelected={false} value="software">
              {getString("myOrders.softwareToggle")}
            </ToggleButton>
            <ToggleButton isSelected={true} value="hardware">
              {getString("myOrders.hardwareToggle")}
            </ToggleButton>
          </ToggleButtonGroup>
          <Spacer />
          <Link href="#" data-testid="get-order-support" onClick={() => setShowFlyout(true)}>
            Get order support
          </Link>
        </Row>

        <Row padding="S300" gridGap="S400" width="100%">
          <Col gridGap="S200" width="100%">
            <MessageBannersBox
              messages={mapBannersToMessages()}
              onRemove={removeBanner}
              strings={{ persistMessage: "If the error persists, ", ctiLinkLabel: "contact support" }}
              ctiUrl=""
            />
            <HardwareOrdersTable orders={orders} isLoading={isLoading} />
          </Col>
        </Row>
      </Col>

      {showFlyout && <HardwareSupportFlyout onClose={() => setShowFlyout(false)} />}
    </Container>
  );
};

export default OrdersPage;
